import { useEffect, useState } from "react";
import { useMessageModal } from "../contexts/MessageModalContext";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import "../styles/adminReviews.css";

interface ReviewMedia {
  id: number;
  mediaType: "photo" | "video";
  filePath: string;
  originalName?: string;
  createdAt?: string;
}

interface ReviewRow {
  id: number;
  orderId: number;
  productId: number;
  buyerEmail: string;
  buyerName: string;
  rating: number;
  comment: string;
  status: "pending" | "approved" | "rejected";
  createdAt: string;
  updatedAt: string;

  subscriberId?: number | string;
  productName?: string;
  barCode?: string;
  productImage?: string;
  quantity?: number;
  unitPrice?: number;
  lineTotal?: number;

  customerDetails?: {
    name?: string;
    email?: string;
    phone?: string;
    address?: string;
  };

  media?: ReviewMedia[];
}

export default function AdminReviews() {
  const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const IMAGE_BASE = import.meta.env.VITE_LYRAZONE_IMAGE_URL;
  const { showModal } = useMessageModal();

  const [data, setData] = useState<ReviewRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<ReviewRow | null>(null);
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const [statusMap, setStatusMap] = useState<Record<number, string>>({});
  const [savingId, setSavingId] = useState<number | null>(null);

  const formatCurrency = (value?: number) =>
    new Intl.NumberFormat("en-MY", {
      style: "currency",
      currency: "MYR",
    }).format(Number(value || 0));

  const toLabel = (value: string) =>
    String(value || "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const fetchReviews = async () => {
    try {
      const res = await fetch(`${BASE}/admin/reviews`, {
        credentials: "include",
      });

      const result = await res.json();
      const freshData = result?.data || [];

      if (result?.success) {
        setData(freshData);
        setSelectedRow((prev) =>
          prev
            ? freshData.find((item: ReviewRow) => item.id === prev.id) || null
            : null,
        );

        setStatusMap((prev) => {
          const next = { ...prev };
          freshData.forEach((item: ReviewRow) => {
            next[item.id] = prev[item.id] || item.status;
          });
          return next;
        });
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("fetchReviews error:", error);
      setData([]);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const toggleExpanded = (reviewId: number) => {
    setExpandedRows((prev) => ({
      ...prev,
      [reviewId]: !prev[reviewId],
    }));
  };

  const handleStatusChange = (reviewId: number, value: string) => {
    setStatusMap((prev) => ({
      ...prev,
      [reviewId]: value,
    }));
  };

  const handleUpdateStatus = async (row: ReviewRow) => {
    try {
      setSavingId(row.id);

      const res = await fetch(`${BASE}/admin/reviews/${row.id}/status`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          status: statusMap[row.id] || row.status,
        }),
      });

      const result = await res.json();

      if (result?.success) {
        await fetchReviews();
        showModal({
          type: "success",
          title: "Success",
          message: "Review status updated successfully.",
        });
      } else {
        showModal({
          type: "error",
          title: "Error",
          message: result?.message || "Unable to update review status.",
        });
      }
    } catch (error) {
      console.error("handleUpdateStatus error:", error);
      showModal({
        type: "error",
        title: "Error",
        message: "Unable to update review status.",
      });
    } finally {
      setSavingId(null);
    }
  };

  const renderStatusBadge = (status: string) => {
    const normalized = String(status || "").toLowerCase();

    const className =
      normalized === "approved"
        ? "badge-success"
        : normalized === "rejected"
          ? "badge-danger"
          : "badge-warning";

    return <span className={className}>{toLabel(status)}</span>;
  };

  const renderStars = (rating: number) => {
    return (
      <div className="admin-reviews-stars">
        {Array.from({ length: 5 }, (_, index) => {
          const active = index + 1 <= Number(rating || 0);
          return (
            <span
              key={index}
              className={`admin-reviews-star ${active ? "active" : ""}`}
            >
              ★
            </span>
          );
        })}
      </div>
    );
  };

  const getMediaUrl = (filePath?: string) => {
    if (!filePath) return "";
    if (filePath.startsWith("http")) return filePath;
    return `${IMAGE_BASE}${filePath}`;
  };

  return (
    <div>
      <AdminListToolkit
        title="Customer Review"
        columns={[]}
        data={data}
        selectedRow={selectedRow}
        enableExport
        exportFileName="customer-reviews"
      >
        {(filteredData: ReviewRow[]) => (
          <div className="admin-orders-table-wrap">
            <table className="admin-orders-table admin-reviews-table">
              <thead>
                <tr>
                  <th style={{ width: "56px" }}></th>
                  <th>Subscriber ID</th>
                  <th>Subscriber Name</th>
                  <th>Product Name</th>
                  <th>Review</th>
                  <th>Status</th>
                  <th style={{ width: "220px" }}>Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="admin-orders-empty">
                      No data available
                    </td>
                  </tr>
                ) : (
                  filteredData.map((row) => {
                    const expanded = !!expandedRows[row.id];

                    return (
                      <FragmentRow
                        key={row.id}
                        row={
                          <>
                            <tr
                              className={`admin-orders-parent-row ${
                                selectedRow?.id === row.id ? "selected" : ""
                              }`}
                              onClick={() => setSelectedRow(row)}
                            >
                              <td>
                                <button
                                  type="button"
                                  className="admin-orders-expand-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleExpanded(row.id);
                                  }}
                                >
                                  {expanded ? "-" : "+"}
                                </button>
                              </td>

                              <td>{row.subscriberId || "-"}</td>
                              <td>{row.buyerName || "-"}</td>
                              <td>{row.productName || "-"}</td>
                              <td className="admin-reviews-comment-cell">
                                {row.comment || "-"}
                              </td>
                              <td>{renderStatusBadge(row.status)}</td>
                              <td>
                                <div className="admin-reviews-status-action">
                                  <select
                                    className="admin-reviews-status-select"
                                    value={statusMap[row.id] || row.status}
                                    onChange={(e) =>
                                      handleStatusChange(row.id, e.target.value)
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <option value="pending">Pending</option>
                                    <option value="approved">Approved</option>
                                    <option value="rejected">Rejected</option>
                                  </select>

                                  <button
                                    type="button"
                                    className="admin-reviews-update-btn"
                                    disabled={savingId === row.id}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleUpdateStatus(row);
                                    }}
                                  >
                                    {savingId === row.id
                                      ? "Updating..."
                                      : "Update"}
                                  </button>
                                </div>
                              </td>
                            </tr>

                            {expanded && (
                              <tr className="admin-orders-children-row">
                                <td colSpan={7}>
                                  <div className="admin-orders-children-box">
                                    <div className="admin-orders-delivery">
                                      <h4>Customer Details</h4>
                                      <p>
                                        <strong>Name:</strong>{" "}
                                        {row.customerDetails?.name ||
                                          row.buyerName ||
                                          "-"}
                                      </p>
                                      <p>
                                        <strong>Email:</strong>{" "}
                                        {row.customerDetails?.email ||
                                          row.buyerEmail ||
                                          "-"}
                                      </p>
                                      <p>
                                        <strong>Phone:</strong>{" "}
                                        {row.customerDetails?.phone || "-"}
                                      </p>
                                      <p>
                                        <strong>Address:</strong>{" "}
                                        {row.customerDetails?.address || "-"}
                                      </p>
                                      <p>
                                        <strong>Rating:</strong>{" "}
                                        {renderStars(row.rating)}
                                      </p>
                                      <p>
                                        <strong>Created:</strong>{" "}
                                        {row.createdAt
                                          ? new Date(
                                              row.createdAt,
                                            ).toLocaleString()
                                          : "-"}
                                      </p>
                                    </div>

                                    <div className="admin-orders-items">
                                      <h4>Review Details</h4>

                                      <div className="admin-orders-item-card">
                                        <div className="admin-orders-item-image">
                                          {row.productImage ? (
                                            <img
                                              src={`${IMAGE_BASE}/uploads/products/${row.productId}/${row.productImage}`}
                                              alt={row.productName || "Product"}
                                            />
                                          ) : (
                                            <div className="admin-orders-item-placeholder">
                                              No Image
                                            </div>
                                          )}
                                        </div>

                                        <div className="admin-orders-item-info">
                                          <div className="admin-orders-item-title">
                                            {row.productName || "-"}
                                          </div>
                                          <div className="admin-orders-item-meta">
                                            BarCode: {row.barCode || "-"}
                                          </div>
                                          <div className="admin-orders-item-meta">
                                            Qty: {row.quantity || 0}
                                          </div>
                                          <div className="admin-orders-item-meta">
                                            Unit Price:{" "}
                                            {formatCurrency(row.unitPrice)}
                                          </div>
                                          <div className="admin-orders-item-meta">
                                            Line Total:{" "}
                                            {formatCurrency(row.lineTotal)}
                                          </div>
                                          <div className="admin-orders-item-meta">
                                            Review: {row.comment || "-"}
                                          </div>

                                          {!!row.media?.length && (
                                            <div className="admin-review-media-wrap">
                                              {row.media.map((media) =>
                                                media.mediaType === "photo" ? (
                                                  <a
                                                    key={media.id}
                                                    href={getMediaUrl(
                                                      media.filePath,
                                                    )}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="admin-review-media-card admin-review-media-link"
                                                  >
                                                    <img
                                                      src={getMediaUrl(
                                                        media.filePath,
                                                      )}
                                                      alt={
                                                        media.originalName ||
                                                        "Review media"
                                                      }
                                                      className="admin-review-media-image"
                                                    />
                                                  </a>
                                                ) : (
                                                  <div
                                                    key={media.id}
                                                    className="admin-review-media-card admin-review-media-video-card"
                                                  >
                                                    <video
                                                      controls
                                                      className="admin-review-media-video"
                                                    >
                                                      <source
                                                        src={getMediaUrl(
                                                          media.filePath,
                                                        )}
                                                      />
                                                    </video>
                                                  </div>
                                                ),
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </>
                        }
                      />
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </AdminListToolkit>
    </div>
  );
}

/**
 * Keeps tbody valid while still allowing fragment return with key.
 */
function FragmentRow({ row }: { row: React.ReactNode }) {
  return <>{row}</>;
}
